//region :: Imports ::
import React, {ReactElement, ReactNode} from 'react';
import MComponent from '../../Toolkit/Components/Component';
import {MCancelIconName, MIcon, MOkIconName} from '../../Toolkit/Icons';
import {DialogPositionType} from 'primereact/dialog';
import Dialog from '../../Toolkit/Components/Controls/Dialog';
import MButton from '../../Toolkit/Components/Controls/Button';
import {ExcelIconName} from '../SafeDigConstants';
import MFormControl from '../../Toolkit/Components/Controls/FormControl';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import {MNullable} from '../../Toolkit/Utils';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import ExcelImport from '../ExcelImport';
import {DownloadIconName} from '../SafeDigConstants';
import {utils, writeFile} from 'xlsx';

//endregion

/** */
type Props = {}

/** */
type State = {
    position?: DialogPositionType;
    visible: boolean;
    data: ExcelImport;
    worksheetList: MDropDownItem<number>[];
    selectedSheet: number;
    onDialogComplete?: (data: ExcelImport) => void;
}

/** */
class ImportDialog extends MComponent<Props, State> {

    private ExcelIcon: MIcon = {name: ExcelIconName, color: 'mcolor-info'};

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.initializeState();
    }

    /** */
    private bindMethods(): void {
        this.dialog_onHide = this.dialog_onHide.bind(this);

        this.getDialogTemplate = this.getDialogTemplate.bind(this);
        this.onFileChange = this.onFileChange.bind(this);
        this.isWorkSheetDisabled = this.isWorkSheetDisabled.bind(this);
        this.isOkDisabled = this.isOkDisabled.bind(this);
        this.onReadComplete = this.onReadComplete.bind(this);
        this.onWorkSheetChange = this.onWorkSheetChange.bind(this);
        this.getFooterTemplate = this.getFooterTemplate.bind(this);
        this.ok_Click = this.ok_Click.bind(this);

        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
    }

    /** */
    private initializeState(): void {
        this.state = {
            visible: false,
            data: new ExcelImport(),
            worksheetList: [{label: 'Select Excel File', value: 0}],
            selectedSheet: -1
        };
    }

    /** */
    private dialog_onHide(): void {
        this.safeSetState((draft: State) => draft.visible = false);
    }

    /** */
    public show(inputCallback: (data: ExcelImport) => void, position?: DialogPositionType) {

        let initialDate = new Date();
        initialDate.setDate(initialDate.getDate() + 28);

        this.safeSetState((draft: State) => {
            draft.position = position;
            draft.visible = true;
            draft.onDialogComplete = inputCallback;
        });

    }

    public hide() {
        this.safeSetState((draft: State) => {
            draft.visible = false;
        });
    }

    private ok_Click() {
        this.state.onDialogComplete!(this.state.data);

        this.hide();
    }

    private template_Click() {
        const workbook = utils.book_new();
        const worksheet = utils.aoa_to_sheet([['ID', 'Project', 'Requestor', 'Address1', 'Address2',
                                               'Address3', 'City', 'BuildArea', 'Postcode', 'WorkDescription',
                                               'RequiredDate', 'Easting', 'Easting2', 'Northing', 'Northing2']]);
        utils.book_append_sheet(workbook, worksheet, 'SafeDigs');

        //Set column widths
        worksheet['!cols'] = [
            {wch: 10}, {wch: 12}, {wch: 10}, {wch: 12}, {wch: 10},
            {wch: 12}, {wch: 10}, {wch: 10}, {wch: 10}, {wch: 16},
            {wch: 12}, {wch: 10}, {wch: 10}
        ];

        const projectList = utils.aoa_to_sheet([['Other','1'],['Barrow_Dalton_67_2082','2'],['Ashford_67_2083','3'],['Canterbury_67_2081','4'],['Lancaster_67_2085','5'],
                ['Aintree_67_2086','6'],['Sefton_Park_67_2094','7'],['Stoneycroft_67_2100','8'],['Service_Delivery_67_2097','9']]);
        utils.book_append_sheet(workbook, projectList, 'Projects');

        const buildAreas = utils.aoa_to_sheet([['Aintree'], ['Ashford'], ['Canterbury'], ['Barrow/Dalton'], ['Lancaster'],
                                               ['Stoneycroft'], ['Sefton Park'], ['Service Delivery'], ['Other']]);
        utils.book_append_sheet(workbook, buildAreas, 'Build Areas');

        writeFile(workbook, 'SafeDigsTemplate.xlsx', {compression: true});
    }

    private getDialogTemplate(): ReactNode {
        return (
            <div className="formgrid grid">
                <div>
                    <p><b>Select file to import (must be .xslx):</b></p>
                    <input type="file" onChange={this.onFileChange} accept={'.xlsx'}/>
                    <br/>
                    <br/>
                </div>
                <div className="col-12 md:col-12">
                    <MFormControl label={'Worksheet'} id={'WorkSheetSelect'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MDropDown<number> id="WorkSheetDropdown"
                                                   disabled={this.isWorkSheetDisabled}
                                                   onChange={this.onWorkSheetChange}
                                                   items={this.state.worksheetList}/>
                            );
                        }}
                    </MFormControl>
                </div>
            </div>
        );
    }

    private getFooterTemplate(): ReactNode {
        return (
            <div>
                <MButton label="Template"
                         icon={DownloadIconName}
                         disabled={false}
                         onClick={this.template_Click}/>
                <MButton label="OK"
                         color="success"
                         icon={MOkIconName}
                         disabled={this.isOkDisabled}
                         onClick={this.ok_Click}/>
                <MButton label="Cancel"
                         icon={MCancelIconName}
                         disabled={false}
                         onClick={this.hide}/>
            </div>
        );
    }

    private async onFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        if(event.target.files![0].name.endsWith('xlsx')) {
            this.state.data.ReadFile(event.target.files![0], this.onReadComplete);
        }
    }

    private onReadComplete(sheetList: string[]) {
        const sheetDropdownList: MDropDownItem<number>[] = [];
        sheetList.forEach((name, index) => {
            sheetDropdownList.push({label: name, value: index});
        });
        this.safeSetState((draft: State) => draft.worksheetList = sheetDropdownList);
    }

    private onWorkSheetChange(newValue: MNullable<number>): void {
        this.state.data.SelectedSheet = newValue!;
        this.safeSetState(
            (draft: State) => draft.selectedSheet = newValue!);
    }

    private isWorkSheetDisabled(): boolean {
        return this.state.data === undefined;
    }

    private isOkDisabled(): boolean {
        return this.state.data === undefined || this.state.selectedSheet === -1;
    }

    /** */
    public render(): ReactElement {

        return (
            <Dialog title={'Excel file import'}
                    content={this.getDialogTemplate()}
                    icon={this.ExcelIcon}
                    footer={this.getFooterTemplate()}
                    position={this.state.position}
                    canClose={false}
                    closeOnEscape={false}
                    visible={this.state.visible}
                    onHide={this.dialog_onHide}/>
        );

    }

}

export default ImportDialog;
